import { useMemo, useState, useRef, useLayoutEffect } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';

// Grid'in GÖRÜNEN (filtre/sıralama uygulanmış) satırlarını bar/çizgi/pasta ile çizer.
// Recharts (projede zaten kurulu) — yeni bağımlılık yok. Kategori ekseni bir metin kolonu,
// değerler bir veya daha çok sayısal kolondur; kullanıcı seçer.

const PALETTE = ['#0073ea', '#00c875', '#a25ddc', '#fdab3d', '#e2445c', '#00c2c2', '#579bfc', '#ff5ac4'];
const nf = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 2 });

export function ReportChart({ rows, columns }) {
  const numericCols = useMemo(() => columns.filter((c) => c.num || c.pct), [columns]);
  const categoryCols = useMemo(() => columns.filter((c) => !c.num && !c.pct), [columns]);

  const [type, setType] = useState('bar');
  const [categoryKey, setCategoryKey] = useState(() => (categoryCols[0]?.key || columns[0]?.key));
  const [valueKeys, setValueKeys] = useState(() => numericCols.slice(0, 2).map((c) => c.key));

  const valCols = valueKeys.map((k) => columns.find((c) => c.key === k)).filter(Boolean);

  // Kategori bazında topla (aynı kategori birden çok satırda ise değerleri toplar) — pasta ve
  // az kategorili bar için anlamlı; çok kategoride ilk 30 gösterilir (grafik okunaklı kalsın).
  const data = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      const key = String(r[categoryKey] ?? '—');
      const cur = map.get(key) || { name: key };
      valueKeys.forEach((vk) => { cur[vk] = (cur[vk] || 0) + (Number(r[vk]) || 0); });
      map.set(key, cur);
    });
    return [...map.values()].slice(0, 30);
  }, [rows, categoryKey, valueKeys]);

  const toggleValue = (k) => setValueKeys((cur) => (cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]));

  // Grafiği PNG indir: Recharts SVG'sini serileştir → canvas'a çiz → indir. Kütüphane yok.
  const downloadPng = () => {
    const svg = canvasRef.current?.querySelector('svg');
    if (!svg) return;
    const clone = svg.cloneNode(true);
    const w = svg.clientWidth || 900; const h = svg.clientHeight || 420;
    clone.setAttribute('width', w); clone.setAttribute('height', h);
    // Arkaplanı beyaz yap (şeffaf PNG yerine).
    const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    rect.setAttribute('width', w); rect.setAttribute('height', h); rect.setAttribute('fill', '#ffffff');
    clone.insertBefore(rect, clone.firstChild);
    const xml = new XMLSerializer().serializeToString(clone);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = w * 2; canvas.height = h * 2; // 2x netlik
      const ctx = canvas.getContext('2d');
      ctx.scale(2, 2); ctx.drawImage(img, 0, 0);
      const a = document.createElement('a');
      a.href = canvas.toDataURL('image/png');
      a.download = 'rapor-grafik.png';
      a.click();
    };
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(xml)}`;
  };

  // Recharts ResponsiveContainer height="100%" flex parent'ta 0'a çöküyor → canvas'ı ölçüp
  // gerçek pikselle besle (çalışan repo deseni sabit yükseklik veriyor).
  const canvasRef = useRef(null);
  const [chartH, setChartH] = useState(420);
  useLayoutEffect(() => {
    const el = canvasRef.current;
    if (!el) return undefined;
    const update = () => setChartH(Math.max(280, el.clientHeight - 24));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div className="rg-chart">
      <div className="rg-chart-bar">
        <div className="rg-chart-types">
          {[['bar', 'Bar'], ['line', 'Çizgi'], ['pie', 'Pasta']].map(([t, lbl]) => (
            <button key={t} type="button" className={`rg-chip-btn${type === t ? ' on' : ''}`} onClick={() => setType(t)}>{lbl}</button>
          ))}
        </div>
        <label className="rg-chart-field">
          Kategori
          <select value={categoryKey} onChange={(e) => setCategoryKey(e.target.value)}>
            {(categoryCols.length ? categoryCols : columns).map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </label>
        <div className="rg-chart-values">
          <span className="rg-chart-values-label">Değerler:</span>
          {numericCols.map((c) => (
            <button key={c.key} type="button" className={`rg-chip-btn sm${valueKeys.includes(c.key) ? ' on' : ''}`} onClick={() => toggleValue(c.key)}>{c.label}</button>
          ))}
        </div>
        <button type="button" className="rg-chip-btn" style={{ marginLeft: 'auto' }} onClick={downloadPng}>⬇ PNG</button>
      </div>

      <div className="rg-chart-canvas" ref={canvasRef}>
        {valCols.length === 0
          ? <div className="rg-chart-empty">En az bir sayısal değer seçin.</div>
          : (
            <ResponsiveContainer width="100%" height={chartH}>
              {type === 'pie' ? (
                <PieChart>
                  <Tooltip formatter={(v) => nf.format(v)} />
                  <Legend />
                  <Pie data={data} dataKey={valCols[0].key} nameKey="name" cx="50%" cy="50%" outerRadius="80%" label>
                    {data.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                  </Pie>
                </PieChart>
              ) : type === 'line' ? (
                <LineChart data={data} margin={{ top: 10, right: 20, bottom: 4, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color, #e5e7eb)" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => nf.format(v)} />
                  <Tooltip formatter={(v) => nf.format(v)} />
                  <Legend />
                  {valCols.map((c, i) => <Line key={c.key} dataKey={c.key} name={c.label} stroke={PALETTE[i % PALETTE.length]} strokeWidth={2} dot={false} />)}
                </LineChart>
              ) : (
                <BarChart data={data} margin={{ top: 10, right: 20, bottom: 4, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color, #e5e7eb)" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => nf.format(v)} />
                  <Tooltip formatter={(v) => nf.format(v)} />
                  <Legend />
                  {valCols.map((c, i) => <Bar key={c.key} dataKey={c.key} name={c.label} fill={PALETTE[i % PALETTE.length]} radius={[4, 4, 0, 0]} />)}
                </BarChart>
              )}
            </ResponsiveContainer>
          )}
      </div>
    </div>
  );
}
